import { useForm } from 'react-hook-form';
import AutoComplete, {
  IAutoComplete,
} from '@monofront/design-system/components/Form/AutoComplete';
import Checkbox from '@monofront/design-system/components/Form/Checkbox';
import LabelV1 from '@monofront/design-system/components/Form/Label';
import {
  handleKeyDownForRegex,
  DIGIT_REGEX,
  TEXT_REGEX,
  FEATURE_FLAG_MAPBOX_AUTO_COMPLETE,
  CHECKOUT_ADDRESS_FORM_CTA_ALWAYS_ACTIVE,
} from '@monofront/business/constants';
import { IAddressV2, ISuggestedAddress } from '@monofront/types';
import CountriesSelectInputABTest from './CountriesSelectInputABTest';
import {
  useClientLayoutEffect,
  useSuggestedAddresses,
} from '@monofront/business/hooks';
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js/min';
import { postcodeValidator } from 'postcode-validator';
import { useTranslations } from 'next-intl';
import useI18n from '@hooks/i18n/useI18n';
import { useEffect, useState } from 'react';
import { keys } from 'radash';
import getConfig from 'next/config';
import { v4 as uuid } from 'uuid';
import { useFeatureIsOn } from '@monofront/business/contexts';
import {
  ErrorText,
  Input,
  Label,
  TextInput,
} from '@monofront/design-system/components/FormV2';
import Tooltip from '@monofront/design-system/components/Tooltip';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import Help from '@monofront/design-system/visuals/Icons/Help';
import { trackEvent } from '@monofront/tracking';

const sessionToken = uuid();

const AddressAutoComplete = AutoComplete as IAutoComplete<{
  street: string;
  postcode: string;
  city: string;
  countryCode: string;
}>;

export interface IFormAddress extends IAddressV2 {
  formContainer?: string;
}

interface IAddressFormProps {
  isLoading: boolean;
  onSubmitAddress: (address: IAddressV2) => void;
  onCancel?: () => void;
  defaultValues?: Partial<IFormAddress>;
  hasCancelButton?: boolean;
  hasDefaultCheckbox?: boolean;
}

const {
  publicRuntimeConfig: { mapboxToken },
} = getConfig();

const AddressFormABTest: React.FC<IAddressFormProps> = ({
  onSubmitAddress,
  onCancel,
  defaultValues,
  hasCancelButton = false,
  hasDefaultCheckbox = false,
}) => {
  const [isCompanyNameFieldDisplayed, setIsCompanyNameFieldDisplayed] =
    useState(Boolean(defaultValues?.company));
  const tGlobal = useTranslations('Global');
  const tAccount = useTranslations('Account');
  const { isGermanMarket, countryCode, locale, availableCountryLocale } =
    useI18n();
  const isMapboxAutocompleteEnabled = useFeatureIsOn(
    FEATURE_FLAG_MAPBOX_AUTO_COMPLETE,
  );
  const isAddressFormCTAAlwaysActive = useFeatureIsOn(
    CHECKOUT_ADDRESS_FORM_CTA_ALWAYS_ACTIVE,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    trigger,
    formState,
  } = useForm<IFormAddress>({
    defaultValues,
    mode: 'onBlur',
  });

  const {
    writtenStreet,
    setWrittenStreet,
    suggestedAddresses,
    isSuggestedAddressLoading,
  } = useSuggestedAddresses({
    mapboxToken,
    locale,
    sessionToken,
    searchableCountry: availableCountryLocale,
  });

  useEffect(() => {
    for (const error of Object.values(errors)) {
      if ('ref' in error && error.ref) {
        (error.ref as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
        break;
      }
    }
  }, [errors]);

  useClientLayoutEffect(() => {
    if (defaultValues) {
      const keysToValidate = keys(defaultValues) as Array<
        keyof Partial<IFormAddress>
      >;

      trigger(keysToValidate);
    }
  }, [defaultValues]);

  useEffect(() => {
    trackEvent({ type: 'LFStartShippingAddress' });
  }, []);

  const onSubmit = ({
    formContainer: _formContainer,
    ...address
  }: IFormAddress) => {
    trackEvent({ type: 'LFSubmitShippingAddress' });

    return onSubmitAddress(
      Object.keys(address).reduce(
        (acc, key) => ({
          ...acc,
          [key]: address[key].trim?.() ?? address[key],
        }),
        {} as IFormAddress,
      ),
    );
  };

  const onSuggestedAddressSelect = ({
    street,
    postcode,
    city,
    countryCode = 'FR',
  }: ISuggestedAddress) => {
    setValue('street', street);
    setValue('postcode', postcode);
    setValue('city', city);
    setValue('countryCode', countryCode);
    setWrittenStreet('');
    trigger(['street', 'postcode', 'city', 'countryCode']);
  };

  const handleTriggerValidation = async () => {
    await trigger();

    // Retrieve the first field with an error and focus on it
    const firstErrorKey = (
      Object.keys(errors) as Array<keyof typeof errors>
    ).reduce<keyof typeof errors | null>((field, a) => {
      const fieldKey = field as keyof typeof errors;

      return errors[fieldKey] ? fieldKey : a;
    }, null);

    if (firstErrorKey) {
      (
        document.querySelector(
          `input[name="${firstErrorKey}"]`,
        ) as HTMLInputElement | null
      )?.focus();
    }
  };

  return (
    <div>
      {errors.formContainer && (
        <p className="tw-lf-error-message">{errors.formContainer.message}</p>
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="tw-flex tw-w-full tw-flex-col tw-gap-4"
      >
        <div
          className="tw-flex tw-flex-col tw-gap-4 desktop:tw-flex-row"
          data-testname="Address form inputs"
        >
          <TextInput
            id="firstName"
            isInvalid={Boolean(errors.firstName)}
            UNSAFE_className="tw-w-full"
          >
            <Label>{tAccount('firstname')}</Label>
            <Input
              autoComplete="given-name"
              type="text"
              placeholder={tAccount('firstnamePlaceholder')}
              onKeyDown={handleKeyDownForRegex(TEXT_REGEX)}
              {...register('firstName', {
                required: {
                  value: true,
                  message: tAccount('firstNameRequiredMessage'),
                },
                validate: {
                  isValid: (value) =>
                    TEXT_REGEX.test(value.trim()) ||
                    tAccount('firstNamePatternMessage'),
                },
                minLength: {
                  value: 2,
                  message: tAccount('shouldContainsAtLeast2chars'),
                },
              })}
              dataTestId="AddressForm-Input-first-name"
            />
            {errors.firstName && (
              <ErrorText dataTestId="Address form firstname input error message">
                {errors.firstName.message}
              </ErrorText>
            )}
          </TextInput>
          <TextInput
            id="lastName"
            isInvalid={Boolean(errors.lastName)}
            UNSAFE_className="tw-w-full"
          >
            <Label>{tAccount('lastname')}</Label>
            <Input
              autoComplete="family-name"
              type="text"
              placeholder={tAccount('lastNamePlaceholder')}
              onKeyDown={handleKeyDownForRegex(TEXT_REGEX)}
              {...register('lastName', {
                required: true,
                validate: {
                  isValid: (value) => TEXT_REGEX.test(value.trim()),
                },
                minLength: 2,
              })}
              dataTestId="AddressForm-Input-last-name"
            />
            {errors.lastName && (
              <ErrorText>{errors.lastName.message}</ErrorText>
            )}
          </TextInput>
        </div>

        {isCompanyNameFieldDisplayed || isGermanMarket ? (
          <TextInput id="company">
            <Label>{tAccount('companyName')}</Label>
            <Input
              autoComplete="organization"
              type="text"
              placeholder={tAccount('companyNamePlaceholder')}
              {...register('company')}
              dataTestId="AddressForm-Input-company-name"
            />
          </TextInput>
        ) : (
          <button
            onClick={() => setIsCompanyNameFieldDisplayed(true)}
            className="tw-lf-link-md tw-self-start"
          >
            {tAccount('displayCompanyNameField')}
          </button>
        )}
        <TextInput id="street" isInvalid={Boolean(errors.street)}>
          <Label>{tAccount('addressInputLabel')}</Label>
          {isGermanMarket && !isMapboxAutocompleteEnabled ? (
            <Input
              autoComplete="off"
              type="text"
              placeholder={tAccount('suggestedAddressPlaceholder')}
              {...register('street', {
                required: {
                  value: true,
                  message: tAccount('suggestedAddressRequiredMessage'),
                },
                maxLength: {
                  value: 32,
                  message: tAccount('suggestedAddressPatternMessage'),
                },
              })}
              onChange={(e) => setWrittenStreet(e.target.value)}
              dataTestId="AddressForm-Input-suggested-address-pattern-message"
            />
          ) : (
            <AddressAutoComplete
              isLoading={isSuggestedAddressLoading}
              options={
                writtenStreet?.length
                  ? suggestedAddresses?.map(
                      ({
                        label,
                        street,
                        postcode,
                        city,
                        countryCode,
                        type,
                      }) => ({
                        id: label,
                        label,
                        value: {
                          street,
                          postcode,
                          city,
                          countryCode: countryCode || 'FR',
                          type,
                        },
                      }),
                    )
                  : undefined
              }
              handleSelect={onSuggestedAddressSelect}
              renderInput={(params) => (
                <Input
                  {...params}
                  autoComplete="off"
                  type="text"
                  placeholder={tAccount('suggestedAddressPlaceholder')}
                  {...register('street', {
                    required: {
                      value: true,
                      message: tAccount('suggestedAddressRequiredMessage'),
                    },
                    maxLength: {
                      value: 32,
                      message: tAccount('suggestedAddressPatternMessage'),
                    },
                  })}
                  onChange={(e) => setWrittenStreet(e.target.value)}
                  dataTestId="AddressForm-Input-street"
                />
              )}
            />
          )}
          {errors.street && <ErrorText>{errors.street.message}</ErrorText>}
        </TextInput>
        <div
          className="tw-flex tw-flex-col tw-gap-4 desktop:tw-flex-row"
          data-testname="Address form inputs"
        >
          <TextInput
            id="postcode"
            isInvalid={Boolean(errors.postcode)}
            UNSAFE_className="tw-w-full"
          >
            <Label>{tAccount('zipCode')}</Label>
            <Input
              onKeyDown={handleKeyDownForRegex(DIGIT_REGEX)}
              autoComplete="postal-code"
              inputMode="numeric"
              type="text"
              placeholder={tAccount('zipCodePlaceholder')}
              {...register('postcode', {
                required: {
                  value: true,
                  message: tAccount('zipCodeRequiredMessage'),
                },
                validate: {
                  isPostalCodeValid: (value) =>
                    !getValues('countryCode') ||
                    postcodeValidator(value, getValues('countryCode')),
                },
              })}
              dataTestId="AddressForm-Input-postcode"
            />
            {errors.postcode && (
              <ErrorText>{errors.postcode.message}</ErrorText>
            )}
          </TextInput>
          <TextInput
            id="city"
            isInvalid={Boolean(errors.city)}
            UNSAFE_className="tw-w-full"
          >
            <Label>{tAccount('city')}</Label>
            <Input
              type="text"
              autoComplete="address-level2"
              placeholder={tAccount('cityPlaceholder')}
              onKeyDown={handleKeyDownForRegex(TEXT_REGEX)}
              {...register('city', {
                required: {
                  value: true,
                  message: tAccount('cityRequiredMessage'),
                },
                pattern: {
                  value: TEXT_REGEX,
                  message: tAccount('cityPatternMessage'),
                },
              })}
              dataTestId="AddressForm-Input-city"
            />
            {errors.city && <ErrorText>{errors.city.message}</ErrorText>}
          </TextInput>
        </div>
        <CountriesSelectInputABTest
          label={tAccount('country')}
          {...register('countryCode', {
            required: {
              value: true,
              message: tAccount('chooseCountry'),
            },
          })}
          isInvalid={Boolean(errors.countryCode)}
        />
        {errors.countryCode && (
          <ErrorText>{errors.countryCode.message}</ErrorText>
        )}
        <TextInput id="phoneNumber" isInvalid={Boolean(errors.phoneNumber)}>
          <span className="tw-flex tw-flex-row">
            <Label>{tAccount('phoneNumber')}</Label>
            <Tooltip
              ariaId="delivery-phone-number"
              content={<p>{tAccount('phonePopOver')}</p>}
              position="top"
              arrowPosition="center"
              eventType="click-only"
              UNSAFE_className="tw-ml-1"
            >
              <button
                onClick={(e) => e.preventDefault()}
                className="tw-lf-button-ghost tw-ml-1 tw-cursor-pointer tw-pt-1"
              >
                <Help size={IconSizes.sm} />
              </button>
            </Tooltip>
          </span>
          <Input
            onKeyDown={handleKeyDownForRegex(DIGIT_REGEX)}
            type="tel"
            placeholder={tAccount('phoneNumberPlaceholder')}
            {...register('phoneNumber', {
              required: {
                value: true,
                message: tAccount('phoneNumberRequiredMessage'),
              },
              validate: (phoneNumber) => {
                const parsedPhoneNumber = parsePhoneNumber(phoneNumber ?? '', {
                  defaultCountry: countryCode as CountryCode,
                });

                return (
                  parsedPhoneNumber?.isValid() ||
                  tAccount('phoneNumberPatternMessage')
                );
              },
            })}
            // Need to force custom validation because react hook form does not work
            onChange={(event) => {
              const parsedPhoneNumber = parsePhoneNumber(event.target.value, {
                defaultCountry: countryCode as CountryCode,
              });

              event.target.value = !parsedPhoneNumber?.isValid()
                ? event.target.value
                : parsedPhoneNumber?.country === countryCode
                ? parsedPhoneNumber.formatNational()
                : parsedPhoneNumber.formatInternational();

              trigger('phoneNumber');
            }}
            dataTestId="AddressForm-Input-phone"
          />
          {errors.phoneNumber && (
            <ErrorText>{errors.phoneNumber.message}</ErrorText>
          )}
        </TextInput>
        {hasDefaultCheckbox && (
          <div className="tw-mb-8 tw-mt-2 tw-flex [&_label]:tw-ml-2">
            <Checkbox
              id="default"
              required={false}
              defaultChecked={Boolean(getValues('default'))}
              {...register('default', { required: false })}
              disabled={Boolean(getValues('default'))}
            />
            <LabelV1 id="default">{tAccount('setAsDefaultAddress')}</LabelV1>
          </div>
        )}
        <div className="tw-mb-2 tw-mt-2 tw-flex tw-flex-row tw-justify-around desktop:tw-mb-0">
          {hasCancelButton ? (
            <div className="tw-mr-4 tw-hidden desktop:tw-block">
              <button
                className="tw-lf-button tw-lf-button-secondary tw-lf-button-md"
                onClick={() => onCancel?.()}
                data-testid="AddressForm-Button-cancel"
              >
                {tGlobal('cancel')}
              </button>
            </div>
          ) : null}
          <button
            type="submit"
            className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-scroll-mt-16"
            data-testid="Submit create address form"
            onClick={handleTriggerValidation}
            disabled={isAddressFormCTAAlwaysActive ? false : !formState.isValid}
          >
            {defaultValues?.firstName
              ? tAccount('addressUpdateSubmit')
              : tAccount('addressCreateSubmit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddressFormABTest;
